import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { NotesApi, newNoteId, type Note, type NoteParentType } from '@/services/shared/notesApi'
import { AuthService } from '@/services/core/auth'

/**
 * The notes hanging off one object, with the writes that keep the object's own
 * `notes` list in step: creating attaches the new note to the parent, removing
 * detaches it and hides it.
 *
 * `noteIds` comes from the parent object as the API returned it. After a write
 * the parent's query is invalidated, so the fresh list arrives from the server
 * rather than being guessed at here.
 */
export function useNotes(
  parent: NoteParentType,
  parentId: string | undefined,
  noteIds: string[],
  /** Query key of the parent object, invalidated after every write. */
  parentQueryKey: readonly unknown[]
) {
  const queryClient = useQueryClient()
  const hasToken = AuthService.isAuthenticated()

  const query = useQuery({
    queryKey: ['notes', parent, parentId, [...noteIds].sort().join(';')],
    queryFn: () => NotesApi.getByIds(noteIds),
    enabled: hasToken && !!parentId,
  })

  const invalidate = () => queryClient.invalidateQueries({ queryKey: parentQueryKey })

  const create = useMutation({
    mutationFn: async (html: string) => {
      const note = await NotesApi.save(newNoteId(), { data: [html], dataType: 'HTML' })
      await NotesApi.setParentNoteIds(parent, parentId!, [...noteIds, note.id])
      return note
    },
    onSuccess: invalidate,
  })

  const update = useMutation({
    mutationFn: ({ id, html }: { id: string; html: string }) =>
      NotesApi.save(id, { data: [html], dataType: 'HTML' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['notes', parent, parentId] })
      await invalidate()
    },
  })

  const remove = useMutation({
    mutationFn: async (id: string) => {
      await NotesApi.setParentNoteIds(parent, parentId!, noteIds.filter((n) => n !== id))
      await NotesApi.hide(id)
    },
    onSuccess: invalidate,
  })

  return {
    notes: (query.data ?? []) as Note[],
    isLoading: query.isLoading && noteIds.length > 0,
    create,
    update,
    remove,
  }
}
